<template>
  <dashboard-page
    title="Atrativos"
    subtitle="passeios e experiências disponíveis"
  >
    <modal-loading
      :openModal="isLoading"
      :value="'100'"
      :label="'carregando... '"
      :variant="'white'"
    >
    </modal-loading>

    <template #actions>
      <div class="d-flex align-items-center search-row">
        <div
          class="d-flex align-items-center w-100"
          style="
            padding: 7px;
            border: 0.5px solid rgb(218, 218, 218);
            border-radius: 5px;
            background-color: white;
            min-width: 20rem;
          "
        >
          <input
            class="search-input w-100"
            style="border: none"
            v-model="searchQuery"
            placeholder="buscar"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="products-search-clear"
            title="Limpar busca"
            @click="clearSearchQuery"
          >
            <i class="fas fa-times"></i>
          </button>
          <i v-else class="fas fa-search text-muted"></i>
        </div>
      </div>

      <div class="d-flex align-items-center products-list-header-actions">
        <button
          type="button"
          class="btn-icon-outline products-list-search-toggle search-row-mobile"
          :class="{ active: showSearchQuery }"
          @click="toggleSearchQuery"
          title="Buscar atrativo"
        >
          <i class="fas fa-search"></i>
        </button>

        <b-row align-self="start" class="row-1 text-right mr-0 mb-0">
          <b-dropdown
            menu
            on
            right
            no-caret
            id="products-list-actions"
            variant="secondary"
          >
            <template v-slot:button-content>
              <svg
                width="5"
                height="19"
                viewBox="0 0 5 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="2.19231" cy="2.19231" r="2.19231" fill="#12576e" />
                <circle cx="2.19231" cy="9.49992" r="2.19231" fill="#12576e" />
                <circle cx="2.19231" cy="16.8078" r="2.19231" fill="#12576e" />
              </svg>
            </template>

            <b-dropdown-item @click="openSalesPageModal" type="default">
              <i class="ni ni-world-2"></i>página de vendas
            </b-dropdown-item>
            <b-dropdown-item @click="openConfirmedDeparturesModal" type="default">
              <i class="fas fa-calendar-check"></i>Saídas confirmadas
            </b-dropdown-item>
          </b-dropdown>
        </b-row>
      </div>
    </template>

    <div
      class="d-flex align-items-center search-row-mobile px-3 mb-3"
      v-if="showSearchQuery"
    >
      <div
        class="d-flex align-items-center w-100"
        style="
          padding: 7px;
          border: 0.5px solid rgb(218, 218, 218);
          border-radius: 5px;
          background-color: white;
        "
      >
        <input
          class="search-input w-100"
          style="border: none"
          v-model="searchQuery"
          placeholder="buscar"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="products-search-clear"
          title="Limpar busca"
          @click="clearSearchQuery"
        >
          <i class="fas fa-times"></i>
        </button>
        <i v-else class="fas fa-search text-muted"></i>
      </div>
    </div>

    <b-container fluid class="mt-2">
      <card
        class="no-border-card"
        body-classes="px-0 pb-1"
        footer-classes="pb-2"
      >
        <div v-if="isMobile && !isLoading" class="products-list-cards">
          <div v-if="!queriedData.length" class="text-center text-muted py-4">
            Nenhum atrativo encontrado.
          </div>
          <products-list-card
            v-for="(product, index) in queriedData"
            :key="product.uuid || product.id || index"
            :product="product"
            :title="getProductTitle(product)"
            :image-url="getProductImage(product)"
            :stats-html="getProductCapacityData(product)"
            :tickets-html="getProductTicketOptions(product)"
            @edit="openModalEditProduct($event, {})"
            @qrcodes="openQrCodes"
            @details="seeProductDetails"
            @public-bookings="openPublicBookings"
          />
        </div>

        <b-table
          v-else-if="!isLoading"
          class="w-100"
          :items="queriedData"
          :fields="tableColumns"
          :busy="isLoading"
          head-variant="light"
          striped
          responsive
          empty-text="nenhum registro encontrado"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>carregando...</strong>
            </div>
          </template>

          <template #cell(product)="data">
            <div class="d-flex align-items-center products-table-product">
              <b-img
                v-if="getProductImage(data.item)"
                :src="getProductImage(data.item)"
                class="products-table-thumb mr-2"
              />
              <div>
                <div class="font-weight-bold">
                  {{ getProductTitle(data.item) }}
                </div>
                <div
                  v-if="data.item.description"
                  class="text-muted products-table-desc"
                >
                  {{ data.item.description }}
                </div>
              </div>
            </div>
          </template>

          <template #cell(type)="data">
            {{ data.item.type || '—' }}
          </template>

          <template #cell(stats)="data">
            <div v-html="getProductCapacityData(data.item) || '—'"></div>
          </template>

          <template #cell(tickets)="data">
            <div v-html="getProductTicketOptions(data.item) || '—'"></div>
          </template>

          <template #cell(acoes)="data">
            <div class="d-flex align-items-center products-table-actions">
              <button
                type="button"
                class="btn-icon-outline"
                title="Editar atrativo"
                @click="openModalEditProduct(data.item, {})"
              >
                <i class="fas fa-pen text-info"></i>
              </button>
              <button
                type="button"
                class="btn-icon-outline"
                title="QR Codes"
                @click="openQrCodes(data.item)"
              >
                <i class="text-info fas fa-qrcode"></i>
              </button>
              <button
                type="button"
                class="btn-icon-outline products-sales-page"
                title="Página de vendas"
                :disabled="!isActiveProduct(data.item)"
                @click="seeProductDetails(data.item)"
              >
                <i class="text-info fas fa-globe"></i>
              </button>
              <button
                type="button"
                class="btn-icon-outline products-visibility"
                :title="
                  isProductVisible(data.item)
                    ? 'Reservas públicas'
                    : 'Produto não visível'
                "
                :disabled="!isProductVisible(data.item)"
                @click="openPublicBookings(data.item)"
              >
                <i
                  :class="
                    isProductVisible(data.item)
                      ? 'text-info fas fa-eye'
                      : 'fas fa-eye-slash'
                  "
                ></i>
              </button>
            </div>
          </template>
        </b-table>

        <div
          slot="footer"
          class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap align-items-center px-0"
        >
          <div class="d-flex align-items-center flex-wrap mb-2 mb-sm-0">
            <p class="mb-0 mr-3" style="color: gray">
              <el-select
                ref="perPageSelect"
                v-model="pagination.perPage"
                placeholder="Por página"
              >
                <el-option
                  class="select-primary"
                  v-for="item in pagination.perPageOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                >
                </el-option>
              </el-select>
              <span
                class="per-page-label font-weight-bold text-primary"
                @click="openPerPageSelect"
              >
                <i class="no no-down-arrow"></i>
                {{ pagination.perPage }}
              </span>
              por página, listando
              <b>{{ from + 1 }}</b> a <b>{{ to }}</b> de
              <span v-if="searchQuery.length">
                <b>{{ total }}</b> item(s) filtrados(s)
              </span>
              <span v-else>
                <b>{{ total }}</b> item(s)
              </span>
            </p>
          </div>
          <base-pagination
            class="pagination-no-border"
            :current="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
            @change="paginationChanged($event)"
          >
          </base-pagination>
        </div>
      </card>
    </b-container>

    <product-qr-code-modal
      v-if="showQrCodeModal"
      :product="productQrCode"
      :showQrCodeModal="showQrCodeModal"
      @closeProductQrCodeModal="closeProductQrCodeModal"
    >
    </product-qr-code-modal>

    <public-bookings-page-modal
      v-if="showSalesPageModal"
      :showModal="showSalesPageModal"
      @close="closeSalesPageModal"
    />

    <public-bookings-page-modal
      v-if="showConfirmedDeparturesModal"
      :showModal="showConfirmedDeparturesModal"
      title="Saídas confirmadas"
      description="Criar reserva do dia"
      link-suffix="/today"
      logo-align="left"
      @close="closeConfirmedDeparturesModal"
    />

    <EditProductPanel
      v-if="showEditProductPanel"
      :product="productToEdit"
      :isOpeningEdit="isOpeningEdit"
      @closeEditProductPanel="closeEditProductPanel()"
      :xFields="xFields"
      @openEditXFieldModal="openEditXFieldModal($event)"
    >
    </EditProductPanel>

    <div>
      <EditXFieldModal
        v-if="showEditXFieldModal"
        :object="productToEdit"
        :coreObject="coreObject"
        :currentXFieldDetail="currentXFieldDetail"
        :xFields="xFields"
        :xFieldsData="xFieldsData"
        @closeEditXFieldModal="closeEditXFieldModal()"
        @saveXFieldsData="saveXFieldsData($event)"
        @emitTmpFiles="emitTmpFiles($event)"
      />
    </div>
  </dashboard-page>
</template>

<script>
import nodeService from '@/services/nodejs'
import { mapGetters, mapActions } from 'vuex'
import { Select, Option } from 'element-ui'
import Card from '@/components/Cards/Card.vue'
import { BasePagination } from '@/components'
import ModalLoading from '../../components/ModalLoading/ModalLoading.vue'
import ProductQrCodeModal from './ProductQrCodeModal.vue'
import PublicBookingsPageModal from './PublicBookingsPageModal.vue'
import ProductsListCard from './ProductsListCard.vue'
import usePaginateTable from '../../Composables/usePaginateTable'
import EditProductPanel from './EditProductPanel.vue'
import EditXFieldModal from '../Components/EditXFieldModal.vue'
import productXFields from '@/views/Products/store/product-xfields.json'
import swal from 'sweetalert2'
import { isMobile } from '../../store/utils/general'
import { visibilityMapFromProducts } from '@/store/utils/productVisibility'
import { fetchFileObjectUrl } from '@/store/utils/publicLogoFile'

export default {
  component: 'ProductsList',

  setup(props, context) {
    const paginateTable = usePaginateTable(context)

    const total = paginateTable.total
    const from = paginateTable.from
    const to = paginateTable.to
    const queriedData = paginateTable.queriedData
    const pagination = paginateTable.getPagination()
    const billItems = paginateTable.getBillsItems()
    const searchQuery = paginateTable.searchQuery

    function createFuseSearch(keys) {
      return paginateTable.createFuseSearch(keys)
    }
    function setBillItems(items) {
      return paginateTable.setBillsItems(items)
    }

    return {
      total,
      from,
      to,
      queriedData,
      pagination,
      billItems,
      searchQuery,
      createFuseSearch,
      setBillItems,
    }
  },

  components: {
    Card,
    BasePagination,
    [Select.name]: Select,
    [Option.name]: Option,
    ModalLoading,
    ProductQrCodeModal,
    PublicBookingsPageModal,
    ProductsListCard,
    EditProductPanel,
    EditXFieldModal,
    nodeService,
  },

  data() {
    return {
      productTmpFiles: {},
      productQrCode: null,
      showQrCodeModal: false,
      showSalesPageModal: false,
      showConfirmedDeparturesModal: false,
      productToEdit: null,
      showEditProductPanel: false,
      showEditXFieldModal: false,
      isOpeningEdit: false,
      showSearchQuery: false,
      currentObjectUuid: null,
      currentObject: null,
      coreObject: null,
      xFields: null, //xfields definitions from front DB
      xFieldsData: null, //xfields data from from Object
      currentXFieldDetail: null, //current xfield being edited
      productImageUrls: {},
      productImageObjectUrls: {},
      productImageLoading: {},
      tableColumns: [
        { key: 'product', label: 'Atrativo', thStyle: { width: '28%' } },
        { key: 'type', label: 'Tipo', thStyle: { width: '12%' } },
        { key: 'stats', label: 'Estatísticas', thStyle: { width: '22%' } },
        { key: 'tickets', label: 'Ingressos', thStyle: { width: '22%' } },
        {
          key: 'acoes',
          label: '',
          sortable: false,
          thStyle: { width: '16%' },
        },
      ],
    }
  },

  watch: {
    poisList(newValue, oldValue) {
      if (newValue) {
        this.recreateSearchableTable()
      }
    },
  },

  beforeDestroy() {
    this.clearProductImageUrls()
  },

  computed: {
    isMobile() {
      return isMobile()
    },
    ...mapGetters('product', {
      poisList: 'getPois',
      isLoading: 'getIsLoading',
    }),

    ...mapGetters('auth', {
      place: 'getPlace',
      userDetails: 'getUserDetails',
    }),

    poisByProducPlace() {
      return this.groupBy(this.poisList, (poi) => poi.product_place_name)
    },
  },

  methods: {
    ...mapActions('product', ['fetchPOIs', 'setIsLoading']),
    ...mapActions('auth', ['fetchUserDetails']),
    toggleSearchQuery() {
      this.showSearchQuery = !this.showSearchQuery
    },

    clearSearchQuery() {
      this.searchQuery = ''
    },

    openPerPageSelect() {
      const select = this.$refs.perPageSelect
      if (!select) return
      if (typeof select.focus === 'function') {
        select.focus()
      }
      select.visible = true
    },

    abrirModalNovoProduto() {
      this.$root.$emit('modal-product-novo')
    },

    closeProductQrCodeModal() {
      this.showQrCodeModal = false
    },

    closeSalesPageModal() {
      this.showSalesPageModal = false
    },

    openConfirmedDeparturesModal() {
      this.showConfirmedDeparturesModal = true
    },

    closeConfirmedDeparturesModal() {
      this.showConfirmedDeparturesModal = false
    },

    closeEditProductPanel() {
      this.showEditProductPanel = false
    },

    closeEditXFieldModal() {
      this.showEditXFieldModal = false
      this.productTmpFiles = {}
    },

    emitTmpFiles(tmpFiles) {
      Object.keys(tmpFiles || {}).forEach((slug) => {
        if (tmpFiles[slug] == null) {
          this.$delete(this.productTmpFiles, slug)
        } else {
          this.$set(this.productTmpFiles, slug, tmpFiles[slug])
        }
      })
    },

    isImagePickerField(field) {
      return !!(
        field &&
        field.options &&
        field.options.newType == 'image-picker'
      )
    },

    async saveProductFile(slug, public_label, file, options = {}) {
      if (!file || !file.size) return null
      const updateField = options.updateField !== false

      const formData = new FormData()
      formData.append('file', file)

      const fileInfo = {
        name: public_label || slug,
        refId:
          this.currentObjectUuid ||
          (this.currentObject &&
            (this.currentObject.uuid || this.currentObject.id)) ||
          0,
        ref: 'product',
        status: 1,
      }

      const rsp = await nodeService.postFileV3(fileInfo, formData)
      const path = rsp && rsp.data ? rsp.data.path : null

      if (!path) {
        throw new Error('Resposta do upload sem path do arquivo')
      }

      if (updateField && this.xFieldsData && this.xFieldsData[slug]) {
        this.$set(this.xFieldsData[slug], 'value', path)
      }

      return path
    },

    resolveUploadFile(entry, fieldFiles, item) {
      if (entry instanceof Blob) return entry
      if (item && (item.file instanceof Blob)) return item.file
      if (
        item &&
        item.pendingIndex != null &&
        fieldFiles[item.pendingIndex] instanceof Blob
      ) {
        return fieldFiles[item.pendingIndex]
      }
      if (entry && entry.file instanceof Blob) return entry.file
      if (item && item.name && fieldFiles.length) {
        return (
          fieldFiles.find((file) => file && file.name === item.name) || null
        )
      }
      return null
    },

    async uploadImagePickerValues(xFieldsData, files) {
      for (const [key, xfieldData] of Object.entries(xFieldsData || {})) {
        const definition = this.xFields[key] || xfieldData
        if (!this.isImagePickerField(definition)) continue

        const items = Array.isArray(xfieldData.value)
          ? xfieldData.value
          : xfieldData.value
          ? [xfieldData.value]
          : []
        const fieldFiles = Array.isArray(files && files[key]) ? files[key] : []
        const urls = []

        for (const item of items) {
          const file = this.resolveUploadFile(null, fieldFiles, item)
          if (file instanceof Blob) {
            const path = await this.saveProductFile(
              key,
              (xfieldData && xfieldData.public_label) || key,
              file,
              { updateField: false }
            )
            if (path) urls.push(path)
            continue
          }

          if (typeof item === 'string' && item) {
            urls.push(item)
            continue
          }

          if (item && typeof item === 'object') {
            const existing = item.url || item.path
            if (typeof existing === 'string' && existing) urls.push(existing)
          }
        }

        xfieldData.value = urls
        if (this.xFieldsData && this.xFieldsData[key]) {
          this.$set(this.xFieldsData[key], 'value', urls)
        }
      }
    },

    async uploadFormFiles(payload, pickerFiles) {
      const singles = this.productTmpFiles || {}
      for (const slug of Object.keys(singles)) {
        const tmp = singles[slug]
        const file = this.resolveUploadFile(tmp, [], null)
        if (!(file instanceof Blob)) continue

        const path = await this.saveProductFile(
          slug,
          (tmp && tmp.public_label) || slug,
          file
        )
        if (path && payload[slug]) {
          payload[slug].value = path
        }
      }
      this.productTmpFiles = {}

      await this.uploadImagePickerValues(payload, pickerFiles || {})
    },

    async saveXFieldsData(data) {
      const { payload, files } = data
      this.setIsLoading(true)

      try {
        await this.uploadFormFiles(payload, files)
      } catch (error) {
        console.error('Error uploading product file :>> ', error)
        this.setIsLoading(false)
        this.$notify({
          message:
            'Erro ao enviar imagem. Verifique o arquivo e tente novamente.',
          type: 'error',
          duration: 5000,
          overlap: false,
        })
        return
      }

      this.closeEditXFieldModal()
      Object.entries(payload).forEach(([key, xfieldData]) => {
        const fieldType = this.xFields[key] && this.xFields[key].type
        const isFileField = ['file', 'file-image'].includes(fieldType)
        const isImagePicker = this.isImagePickerField(
          this.xFields[key] || xfieldData
        )
        const isColorField = fieldType === 'color'

        if (
          !isFileField &&
          !isImagePicker &&
          !isColorField &&
          ((fieldType && ['text', 'textarea'].includes(fieldType)) ||
            Object.prototype.toString.call(xfieldData.value) ===
              '[object String]')
        ) {
          try {
            xfieldData.value = JSON.parse(xfieldData.value)
          } catch (error) {}
          if (
            Object.prototype.toString.call(xfieldData.value) ===
            '[object Number]'
          ) {
            xfieldData.value = String(xfieldData.value)
          }
        }

        if (this.xFields[key] == undefined) {
          console.warn('unmaped label in xfields :>> ' + key)
        }

        if (
          this.currentObject[key] == undefined &&
          this.currentObject.xfields['laravel_data'][key] == undefined
        ) {
          console.warn('new field inclued in this product :>> ' + key)
        }

        this.currentObject[key] = xfieldData.value
        let sub_key = key.replace(this.coreObject.toLowerCase() + '_', '')
        if (this.currentObject[sub_key] != undefined) {
          this.currentObject[sub_key] = xfieldData.value
        }
        this.currentObject.xfields['laravel_data'][key] =
          xfieldData.value != null ? xfieldData.value : xfieldData
      })

      try {
        await nodeService
          .updateProduct(this.currentObject.xfields.laravel_data)
          .then((response) => {
            this.$notify({
              message: response.data.name + ' atualizado!',
              type: 'success',
              duration: 5000,
              overlap: false,
            })
          })
          .finally(() => {
            this.setIsLoading(false)
          })
      } catch (error) {
        console.error('Error updating product :>> ', error)
        this.setIsLoading(false)
      }

      await this.restorePage()
    },

    getProductKey(product) {
      if (!product) return null
      return product.uuid || product.product_uuid || product.id || null
    },

    resolveXFieldValue(product, key) {
      if (!product || !key) return null

      const unwrap = (raw) => {
        if (raw == null) return null
        if (typeof raw === 'object' && raw.value !== undefined) return raw.value
        return raw
      }

      if (product[key] != null && product[key] !== '') {
        return unwrap(product[key])
      }

      let xfields = product.xfields
      if (typeof xfields === 'string') {
        try {
          xfields = JSON.parse(xfields)
        } catch (error) {
          return null
        }
      }
      if (!xfields) return null

      if (xfields[key] != null) {
        return unwrap(xfields[key])
      }

      const laravel = xfields.laravel_data
      if (laravel && typeof laravel.value === 'string') {
        try {
          const parsed = JSON.parse(laravel.value)
          if (parsed && parsed[key] != null) return unwrap(parsed[key])
        } catch (error) {
          /* laravel_data.value is not JSON */
        }
      }
      if (laravel && laravel[key] != null) {
        return unwrap(laravel[key])
      }

      return null
    },

    resolveProductImageSource(product) {
      if (!product) return ''

      let source = this.resolveXFieldValue(product, 'cover') || ''
      if (!source && product.images && product.images.length > 0) {
        source = product.images[0]
      }

      if (Array.isArray(source)) {
        source = source[0]
      }
      if (source && typeof source === 'object') {
        source =
          source.url || source.src || source.filepath || source.value || ''
      }
      if (typeof source !== 'string' || !source.trim()) return ''

      if (source.indexOf('google.com') > 0) {
        const match = /d\/([^/]+)\/view/.exec(source)
        if (match && match[1]) {
          return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w400`
        }
      }

      return source
    },

    isDirectMediaUrl(value) {
      if (!value || typeof value !== 'string') return false
      return (
        value.indexOf('http://') === 0 ||
        value.indexOf('https://') === 0 ||
        value.indexOf('blob:') === 0 ||
        value.indexOf('data:image') === 0
      )
    },

    getProductImage(product) {
      const key = this.getProductKey(product)
      if (key && this.productImageUrls[key]) {
        return this.productImageUrls[key]
      }

      const source = this.resolveProductImageSource(product)
      if (!source) return null

      if (this.isDirectMediaUrl(source)) {
        return source
      }

      // Enquanto o blob carrega (loadProductImages), usa o placeholder
      return require('@/assets/img/j-logo.png')
    },

    async loadProductImage(product) {
      const key = this.getProductKey(product)
      if (!key || this.productImageLoading[key] || this.productImageUrls[key]) {
        return
      }

      const source = this.resolveProductImageSource(product)
      if (!source) return

      if (this.isDirectMediaUrl(source)) {
        this.$set(this.productImageUrls, key, source)
        return
      }

      this.$set(this.productImageLoading, key, true)
      try {
        const objectUrl = await fetchFileObjectUrl(source)
        if (!objectUrl) return
        if (this.productImageObjectUrls[key]) {
          URL.revokeObjectURL(this.productImageObjectUrls[key])
        }
        this.$set(this.productImageObjectUrls, key, objectUrl)
        this.$set(this.productImageUrls, key, objectUrl)
      } catch (error) {
        console.warn(
          'Não foi possível carregar a imagem do atrativo:',
          key,
          error
        )
      } finally {
        this.$set(this.productImageLoading, key, false)
      }
    },

    loadProductImages(products) {
      ;(products || []).forEach((product) => {
        this.loadProductImage(product)
      })
    },

    clearProductImageUrls() {
      Object.keys(this.productImageObjectUrls || {}).forEach((key) => {
        URL.revokeObjectURL(this.productImageObjectUrls[key])
      })
      this.productImageObjectUrls = {}
      this.productImageUrls = {}
      this.productImageLoading = {}
    },

    getProductTitle(product) {
      var title = `${product.product_name}`
      return title
    },

    getProductCapacityData(product) {
      let prices = ''
      let today = new Date().toISOString()
      let capacity = product.capacity

      if (capacity && capacity[today.substring(0, 7)]) {
        let currentMonth = capacity[today.substring(0, 7)]

        //this month
        let total_booked = new Number(currentMonth.total_booked)
        let total_available = new Number(currentMonth.total_available)
        if (total_booked > 0) {
          prices += `este mês:<br> <b>${total_booked}</b> visitantes (${(
            total_booked /
            (total_booked + total_available)
          )
            .toFixed(2)
            .replace('.', ',')}%)`
        } else {
          prices += `este mês:<br> <b>nenhum</b> visitante`
        }

        total_booked = 0
        total_available = 0
        //today
        Object.keys(currentMonth).forEach((dateTime) => {
          if (dateTime.substring(0, 10) == today.substring(0, 10)) {
            total_booked += new Number(currentMonth[dateTime]['booked'])
            total_available += new Number(currentMonth[dateTime]['available'])
          }
        })
        if (total_booked > 0) {
          prices += `<br>hoje:<br> <b>${total_booked}</b> visitantes (${(
            (1 + total_booked) /
            (total_booked + total_available)
          )
            .toFixed(2)
            .replace('.', ',')}%)`
        } else {
          prices += `<br>hoje:<br> <b>nenhum</b> visitante`
        }
      }
      return prices
    },

    getProductTicketOptions(product) {
      let prices = ''
      if (product.special_prices) {
        product.special_prices[0].forEach((price, idx) => {
          if (idx != 0) {
            prices += ' | '
          }
          prices +=
            '<b>' +
            price.name +
            '</b> R$' +
            Number(price.price).toFixed(2).replace('.', ',')
        })
      }
      return prices
    },

    isActiveProduct(product) {
      if (!product) return false
      return (
        product.active === true ||
        product.active === 'true' ||
        product.active === 1
      )
    },

    isProductVisible(product) {
      if (!product) return false
      const value = product.visibility
      return (
        value === true || value === 'true' || value === 1 || value === '1'
      )
    },

    async loadProductVisibility(placeUuid) {
      if (!placeUuid) return

      try {
        const response = await nodeService.getProductsByPlace(placeUuid)
        const visibilityByUuid = visibilityMapFromProducts(response.data)
        ;(this.poisList || []).forEach((product) => {
          const uuid = product && (product.uuid || product.product_uuid)
          if (!uuid) return
          const key = String(uuid).toUpperCase()
          if (!Object.prototype.hasOwnProperty.call(visibilityByUuid, key)) {
            return
          }
          product.visibility = visibilityByUuid[key]
        })
      } catch (error) {
        console.warn(
          'Não foi possível carregar a visibilidade dos atrativos:',
          error
        )
      }
    },

    openPublicBookings(product) {
      if (!this.isProductVisible(product)) return
      const placeUuid =
        product.place_uuid ||
        product.placeUUID ||
        product.product_place ||
        (this.place && (this.place.uuid || this.place.janooUserUuid)) ||
        ''
      const path = placeUuid
        ? `/public-bookings/${placeUuid}`
        : '/public-bookings'
      window.open(`${window.location.origin}${path}`, '_blank')
    },

    seeProductDetails(product) {
      if (!this.isActiveProduct(product)) return
      const slug = product.product_slug
      var URL = `${process.env.VUE_APP_LARAVEL_URL}lugar/${slug}`
      window.open(URL, '_blank')
    },

    openSalesPageModal() {
      this.showSalesPageModal = true
    },

    openQrCodes(product) {
      this.productQrCode = { ...product }
      this.showQrCodeModal = true
    },

    async loadProductForEdit(productUuid) {
      if (!productUuid) {
        throw new Error('UUID do produto não informado')
      }

      this.currentObjectUuid = productUuid
      this.currentObject =
        (await nodeService.getProduct(this.currentObjectUuid)) || {}

      const laravelData =
        this.currentObject.xfields && this.currentObject.xfields.laravel_data
      this.xFieldsData = laravelData
        ? JSON.parse(JSON.stringify(laravelData))
        : {}

      this.productToEdit = {
        ...(this.productToEdit || {}),
        ...this.currentObject,
        ...(laravelData || {}),
      }

      return this.currentObject
    },

    async openModalEditProduct(product) {
      let productUuid =
        (product && (product.uuid || product.product_uuid)) || null
      productUuid = productUuid.value || productUuid

      if (!productUuid) {
        swal.fire({
          title: 'Erro',
          text: 'Não foi possível identificar o produto para edição.',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonClass: 'btn btn-danger',
        })
        return
      }

      this.setIsLoading(true)
      this.setXFields('Product')
      this.productToEdit = JSON.parse(JSON.stringify(product))

      try {
        await this.loadProductForEdit(productUuid)
        this.showEditProductPanel = true
      } catch (error) {
        console.error('Error loading product for edit panel :>> ', error)
        swal.fire({
          title: 'Erro',
          text: 'Não foi possível abrir a edição do atrativo. Tente novamente.',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonClass: 'btn btn-danger',
        })
      } finally {
        this.setIsLoading(false)
      }
    },

    setXFields(coreObject) {
      this.coreObject = coreObject || 'Product'
      const xFieldsKey = this.coreObject + 'XFields'
      const defaultVendor =
        this.userDetails &&
        this.userDetails[process.env.VUE_APP_UUID_DEFAULT_VENDOR]
      const storedXFields =
        defaultVendor &&
        defaultVendor.user &&
        defaultVendor.user.params &&
        defaultVendor.user.params[xFieldsKey]
          ? defaultVendor.user.params[xFieldsKey]
          : {}
      const correctionKeys = ['display', 'add_display', 'buy_editable']
      const xFields = {}

      // Base: todos os campos do JSON ficam. O vendor padrão corrige flags
      // dos campos em comum e acrescenta os que só existem nele.
      Object.keys(productXFields).forEach((key) => {
        xFields[key] = JSON.parse(JSON.stringify(productXFields[key]))
        const stored = storedXFields[key]
        if (!stored) return
        correctionKeys.forEach((prop) => {
          if (stored[prop] != null) {
            xFields[key][prop] = stored[prop]
          }
        })
      })

      Object.keys(storedXFields).forEach((key) => {
        if (xFields[key]) return
        xFields[key] = JSON.parse(JSON.stringify(storedXFields[key]))
      })

      this.xFields = xFields
    },

    async openEditXFieldModal(event) {
      const productToEdit = this.productToEdit || {}
      const productUuid = event
        ? event.object_uuid
        : productToEdit.uuid
        ? productToEdit.uuid
        : productToEdit.product_uuid || null

      if (!productUuid) {
        this.isOpeningEdit = false
        swal.fire({
          title: 'Erro',
          text: 'Não foi possível identificar o produto para edição.',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonClass: 'btn btn-danger',
        })
        return
      }
      this.isOpeningEdit = true
      this.productTmpFiles = {}

      try {
        this.currentXFieldDetail = event
        this.setXFields(event ? event.coreObject : 'Product')
        await this.loadProductForEdit(productUuid)

        this.showEditProductPanel = false
        this.showEditXFieldModal = true
      } catch (error) {
        console.error('Error opening product xfield modal :>> ', error)
        swal.fire({
          title: 'Erro',
          text: 'Não foi possível abrir a edição do atrativo. Tente novamente.',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonClass: 'btn btn-danger',
        })
      } finally {
        this.isOpeningEdit = false
      }
    },
    perPageChange(perPage) {
      this.pagination.perPage = perPage
      this.pagination.currentPage = 1
    },

    paginationChanged(page) {
      this.pagination.currentPage = page
    },

    recreateSearchableTable() {
      const products =
        this.poisList.length > 0
          ? this.poisList.filter(
              (poi) => poi.type == 'Passeio' || poi.type == 'Experiência'
            )
          : []

      this.setBillItems(products)

      this.createFuseSearch([
        'product_name',
        'product_place_name',
        'type',
        'description',
      ])

      this.loadProductImages(products)
    },

    groupBy(list, keyGetter) {
      const map = new Map()
      list.forEach((item) => {
        const key = keyGetter(item)
        const collection = map.get(key)

        if (!collection) {
          map.set(key, [item])
        } else {
          collection.push(item)
        }
      })
      return Array.from(map)
    },
    async restorePage() {
      this.closeEditXFieldModal()
      this.closeEditProductPanel()
      const PLACE_UUID = this.place.uuid || this.place.janooUserUuid
      await this.fetchPOIs({ PLACE_UUID: PLACE_UUID, PRODUCT_UUID: '' })
      await this.loadProductVisibility(PLACE_UUID)
      this.recreateSearchableTable()
    },
  },

  async mounted() {
    const PLACE_UUID = this.place.uuid || this.place.janooUserUuid
    await this.fetchPOIs({ PLACE_UUID: PLACE_UUID, PRODUCT_UUID: '' })
    await this.loadProductVisibility(PLACE_UUID)
    await this.fetchUserDetails(process.env.VUE_APP_UUID_DEFAULT_VENDOR)
    this.recreateSearchableTable()
  },
}
</script>

<style>
.products-list-header-actions {
  gap: 0.35rem;
}

.products-list-search-toggle.active {
  background-color: #eef5f8;
}

.products-sales-page:disabled,
.products-visibility:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.products-sales-page:disabled:hover,
.products-visibility:disabled:hover {
  background-color: #ffffff;
}

.products-search-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.35rem;
  padding: 0;
  width: 1.35rem;
  height: 1.35rem;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #b11b1b;
  cursor: pointer;
  flex-shrink: 0;
}

.products-search-clear:hover {
  background-color: #f8f1ee;
  color: #b11b1b;
}

.per-page-label {
  cursor: pointer;
  user-select: none;
  margin: 0 0 0.15rem;
  font-weight: normal !important;
  font-size: 1em;
  padding: 0.75rem;
  border-radius: 2px;
}

.per-page-label:hover {
  background-color: #eef5f8 !important;
}

.card-footer .el-select {
  width: 0;
  height: 0;
  overflow: hidden !important;
}

.products-list-cards {
  padding: 0.5rem 0.75rem 0;
}

.no-border-card .table-responsive,
.no-border-card .table.b-table {
  width: 100% !important;
}

.no-border-card .table.b-table {
  table-layout: fixed;
}

.products-table-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
}

.products-table-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 320px;
}

.products-table-actions {
  gap: 0.35rem;
  justify-content: flex-end;
}
</style>
